/**
 * When do services take the offline branch? (Phase 16C C5, design §6.7, decision P3)
 *
 * `enabled()`: a phone or tablet, enrolled, unlocked, and the plan still includes offline mode.
 * On a laptop, or a phone without offline mode, services behave exactly as before (online only)
 * and a write that can't reach the server fails with "You're offline — reconnect to continue".
 *
 * `shouldQueue(visitId)`: enabled and either offline, or the visit already has queued ops
 * (later writes for that visit go through the outbox even online, to keep the order).
 */
import { DeviceContext } from './deviceContext';
import { isOfflineError, isOnline, reconnectError } from './network';
import type { Outbox } from './outbox';

export interface OfflineModeSource {
  unlocked(): boolean;
  outbox(): Outbox | null;
}

let source: OfflineModeSource | null = null;
let planDisabled = false;

export const OfflineMode = {
  /** Wired once by offlineServices (kept separate to avoid an import cycle with the services). */
  bind(next: OfflineModeSource | null): void {
    source = next;
  },
  /** Heartbeat said the plan no longer includes offline mode: stop queueing new work. */
  setPlanDisabled(disabled: boolean): void {
    planDisabled = disabled;
  },
  enabled(): boolean {
    return DeviceContext.isMobile() && !planDisabled && Boolean(source?.unlocked());
  },
  async shouldQueue(visitId?: string | null): Promise<boolean> {
    if (!this.enabled()) return false;
    if (!isOnline()) return true;
    const outbox = source?.outbox();
    if (!visitId || !outbox) return false;
    try {
      return await outbox.hasQueuedForVisit(visitId);
    } catch {
      return false;
    }
  },
};

export interface OfflineBranch<T> {
  visitId?: string | null;
  online: () => Promise<T>;
  local: () => Promise<T>;
}

/**
 * The service pattern from design §6.7: queue when the visit must go through the outbox, else
 * try online; a request that never reached the server is queued (enrolled phone) or turned into
 * the reconnect error (everyone else). Server refusals are thrown unchanged.
 */
export async function runWithOfflineBranch<T>({ visitId, online, local }: OfflineBranch<T>): Promise<T> {
  if (await OfflineMode.shouldQueue(visitId)) return local();
  try {
    return await online();
  } catch (error) {
    if (!isOfflineError(error)) throw error;
    if (OfflineMode.enabled()) return local();
    throw reconnectError();
  }
}
